import type { FleetManagerFieldKey, ShipperFieldKey } from './spreadsheetParser';

export interface DefaultImportValues {
  companyName?: string;
  fullName?: string;
  serviceRegions?: string[];
  fleetSize?: string;
  billingAddress?: string;
}

export interface DefaultShipperImportValues {
  companyName?: string;
  fullName?: string;
  billingAddress?: string;
}

export interface MappedShipperRow {
  rowNumber: number;
  phoneNumber: string;
  companyName: string;
  fullName?: string;
  email?: string;
  gstin?: string;
  billingAddress?: string;
  secondaryPhoneNumber?: string;
  whatsappNumber?: string;
  rawValues: Record<string, string>;
  errors: string[];
  isValid: boolean;
}

export interface ShipperValidationSummary {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  hasErrors: boolean;
  duplicatePhoneCount: number;
  missingRequiredCount: number;
  mappedRows: MappedShipperRow[];
}

export interface MappedFleetManagerRow {
  rowNumber: number;
  phoneNumber: string;
  companyName: string;
  fullName?: string;
  email?: string;
  gstin?: string;
  serviceRegions?: string;
  fleetSize?: number;
  secondaryPhoneNumber?: string;
  whatsappNumber?: string;
  billingAddress?: string;
  rawValues: Record<string, string>;
  errors: string[];
  isValid: boolean;
}

export interface ValidationSummary {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  hasErrors: boolean;
  duplicatePhoneCount: number;
  missingRequiredCount: number;
  mappedRows: MappedFleetManagerRow[];
}

const INDIAN_MOBILE = /^[6-9][0-9]{9}$/;
const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Strips formatting cruft and normalizes to +91XXXXXXXXXX.
 */
export function normalizePhone(raw: string | undefined | null): {
  normalized: string | null;
  error?: string;
} {
  if (!raw || raw.trim() === '') {
    return { normalized: null, error: 'Phone number is required' };
  }

  const digits = raw.replace(/[^0-9]/g, '');
  if (!digits) {
    return { normalized: null, error: 'Invalid phone number format' };
  }

  let tenDigits = '';
  if (digits.length === 10) {
    tenDigits = digits;
  } else if (digits.length === 11 && digits.startsWith('0')) {
    tenDigits = digits.slice(1);
  } else if (digits.length === 12 && digits.startsWith('91')) {
    tenDigits = digits.slice(2);
  } else {
    return { normalized: null, error: `Invalid phone length (${digits.length} digits)` };
  }

  if (!INDIAN_MOBILE.test(tenDigits)) {
    return { normalized: null, error: 'Must be a valid 10-digit Indian mobile (starts with 6-9)' };
  }

  return { normalized: `+91${tenDigits}` };
}

export function validateGstin(raw: string | undefined | null): boolean {
  if (!raw || raw.trim() === '') return true;
  return GSTIN_PATTERN.test(raw.trim().toUpperCase());
}

export function validateEmail(raw: string | undefined | null): boolean {
  if (!raw || raw.trim() === '') return true;
  return EMAIL_PATTERN.test(raw.trim().toLowerCase());
}

/**
 * Transforms raw sheet rows into typed fleet manager records, applies default values,
 * and performs comprehensive pre-validation.
 */
export function mapAndValidateRows(
  rawRows: Record<string, string>[],
  mapping: Record<FleetManagerFieldKey, string>,
  defaults: DefaultImportValues,
): ValidationSummary {
  const mappedRows: MappedFleetManagerRow[] = [];
  const phoneOccurrences = new Map<string, number[]>();

  // Pass 1: Map fields and collect phone occurrences
  rawRows.forEach((row, idx) => {
    const rowNumber = idx + 1;
    const errors: string[] = [];

    // 1. Phone number
    const rawPhone = mapping.phoneNumber ? row[mapping.phoneNumber] : '';
    const phoneRes = normalizePhone(rawPhone);
    const phoneNumber = phoneRes.normalized ?? '';
    if (!phoneRes.normalized) {
      errors.push(phoneRes.error || 'Invalid phone number');
    } else {
      const existing = phoneOccurrences.get(phoneNumber) ?? [];
      existing.push(rowNumber);
      phoneOccurrences.set(phoneNumber, existing);
    }

    // 2. Company name
    const rawCompany = mapping.companyName ? row[mapping.companyName]?.trim() : '';
    const companyName = rawCompany || defaults.companyName?.trim() || '';
    if (!companyName) {
      errors.push('Company name is required');
    }

    // 3. Full name / Contact person
    const rawName = mapping.fullName ? row[mapping.fullName]?.trim() : '';
    const fullName = rawName || defaults.fullName?.trim() || undefined;

    // 4. Secondary phone
    const rawSecPhone = mapping.secondaryPhoneNumber ? row[mapping.secondaryPhoneNumber] : '';
    let secondaryPhoneNumber: string | undefined;
    if (rawSecPhone && rawSecPhone.trim() !== '') {
      const secRes = normalizePhone(rawSecPhone);
      if (secRes.normalized) {
        secondaryPhoneNumber = secRes.normalized;
      } else {
        errors.push(`Invalid secondary phone: ${rawSecPhone.trim()}`);
      }
    }

    // 4b. WhatsApp number
    const rawWaPhone = mapping.whatsappNumber ? row[mapping.whatsappNumber] : '';
    let whatsappNumber: string | undefined;
    if (rawWaPhone && rawWaPhone.trim() !== '') {
      const waRes = normalizePhone(rawWaPhone);
      if (waRes.normalized) {
        whatsappNumber = waRes.normalized;
      } else {
        errors.push(`Invalid WhatsApp number: ${rawWaPhone.trim()}`);
      }
    }

    // 5. Email
    const rawEmail = mapping.email ? row[mapping.email]?.trim().toLowerCase() : '';
    let email: string | undefined = rawEmail || undefined;
    if (email && !validateEmail(email)) {
      errors.push(`Invalid email format: ${email}`);
      email = undefined;
    }

    // 6. GSTIN
    const rawGstin = mapping.gstin ? row[mapping.gstin]?.trim().toUpperCase() : '';
    const gstin: string | undefined = rawGstin || undefined;
    if (gstin && !validateGstin(gstin)) {
      errors.push(`Invalid 15-character GSTIN format: ${gstin}`);
    }

    // 7. Billing Address
    const rawBillingAddress = mapping.billingAddress ? row[mapping.billingAddress]?.trim() : '';
    const billingAddress = rawBillingAddress || defaults.billingAddress?.trim() || undefined;

    // 8. Service Regions
    const rawRegions = mapping.serviceRegions ? row[mapping.serviceRegions]?.trim() : '';
    let serviceRegions: string | undefined = rawRegions || undefined;
    if (!serviceRegions && defaults.serviceRegions && defaults.serviceRegions.length > 0) {
      serviceRegions = defaults.serviceRegions.join(', ');
    }

    // 9. Fleet Size
    const rawFleetSize = mapping.fleetSize ? row[mapping.fleetSize]?.trim() : '';
    const fleetSizeStr = rawFleetSize || defaults.fleetSize?.trim() || '';
    let fleetSize: number | undefined;
    if (fleetSizeStr) {
      const num = parseInt(fleetSizeStr, 10);
      if (isNaN(num) || num < 0) {
        errors.push(`Fleet size must be a non-negative number: ${fleetSizeStr}`);
      } else {
        fleetSize = num;
      }
    }

    // KYC is never taken from the sheet: imported users always start with KYC pending (R47).

    mappedRows.push({
      rowNumber,
      phoneNumber,
      companyName,
      fullName,
      email,
      gstin,
      serviceRegions,
      fleetSize,
      secondaryPhoneNumber,
      whatsappNumber,
      billingAddress,
      rawValues: row,
      errors,
      isValid: errors.length === 0,
    });
  });

  // Pass 2: Check for duplicate phones within file
  let duplicatePhoneCount = 0;
  phoneOccurrences.forEach((rows, phone) => {
    if (rows.length > 1) {
      duplicatePhoneCount += rows.length;
      rows.forEach((rNum) => {
        const row = mappedRows[rNum - 1];
        row.errors.push(`Duplicate phone ${phone} (appears in rows ${rows.join(', ')})`);
        row.isValid = false;
      });
    }
  });

  let validRows = 0;
  let invalidRows = 0;
  let missingRequiredCount = 0;

  mappedRows.forEach((r) => {
    if (r.errors.length > 0) {
      r.isValid = false;
      invalidRows++;
      if (r.errors.some((e) => e.includes('required'))) {
        missingRequiredCount++;
      }
    } else {
      r.isValid = true;
      validRows++;
    }
  });

  return {
    totalRows: mappedRows.length,
    validRows,
    invalidRows,
    hasErrors: invalidRows > 0,
    duplicatePhoneCount,
    missingRequiredCount,
    mappedRows,
  };
}

/**
 * Transforms raw sheet rows into typed shipper records, applies default values,
 * and performs comprehensive pre-validation.
 */
export function mapAndValidateShipperRows(
  rawRows: Record<string, string>[],
  mapping: Record<ShipperFieldKey, string>,
  defaults: DefaultShipperImportValues,
): ShipperValidationSummary {
  const mappedRows: MappedShipperRow[] = [];
  const phoneOccurrences = new Map<string, number[]>();

  // Pass 1: Map fields and collect phone occurrences
  rawRows.forEach((row, idx) => {
    const rowNumber = idx + 1;
    const errors: string[] = [];

    // 1. Phone number
    const rawPhone = mapping.phoneNumber ? row[mapping.phoneNumber] : '';
    const phoneRes = normalizePhone(rawPhone);
    const phoneNumber = phoneRes.normalized ?? '';
    if (!phoneRes.normalized) {
      errors.push(phoneRes.error || 'Invalid phone number');
    } else {
      const existing = phoneOccurrences.get(phoneNumber) ?? [];
      existing.push(rowNumber);
      phoneOccurrences.set(phoneNumber, existing);
    }

    // 2. Company name
    const rawCompany = mapping.companyName ? row[mapping.companyName]?.trim() : '';
    const companyName = rawCompany || defaults.companyName?.trim() || '';
    if (!companyName) {
      errors.push('Company name is required');
    }

    // 3. Full name / Contact person
    const rawName = mapping.fullName ? row[mapping.fullName]?.trim() : '';
    const fullName = rawName || defaults.fullName?.trim() || undefined;

    // 4. Secondary phone
    const rawSecPhone = mapping.secondaryPhoneNumber ? row[mapping.secondaryPhoneNumber] : '';
    let secondaryPhoneNumber: string | undefined;
    if (rawSecPhone && rawSecPhone.trim() !== '') {
      const secRes = normalizePhone(rawSecPhone);
      if (secRes.normalized) {
        secondaryPhoneNumber = secRes.normalized;
      } else {
        errors.push(`Invalid secondary phone: ${rawSecPhone.trim()}`);
      }
    }

    // 4b. WhatsApp number
    const rawWaPhone = mapping.whatsappNumber ? row[mapping.whatsappNumber] : '';
    let whatsappNumber: string | undefined;
    if (rawWaPhone && rawWaPhone.trim() !== '') {
      const waRes = normalizePhone(rawWaPhone);
      if (waRes.normalized) {
        whatsappNumber = waRes.normalized;
      } else {
        errors.push(`Invalid WhatsApp number: ${rawWaPhone.trim()}`);
      }
    }

    // 5. Email
    const rawEmail = mapping.email ? row[mapping.email]?.trim().toLowerCase() : '';
    let email: string | undefined = rawEmail || undefined;
    if (email && !validateEmail(email)) {
      errors.push(`Invalid email format: ${email}`);
      email = undefined;
    }

    // 6. GSTIN
    const rawGstin = mapping.gstin ? row[mapping.gstin]?.trim().toUpperCase() : '';
    const gstin: string | undefined = rawGstin || undefined;
    if (gstin && !validateGstin(gstin)) {
      errors.push(`Invalid 15-character GSTIN format: ${gstin}`);
    }

    // 7. Billing Address
    const rawBillingAddress = mapping.billingAddress ? row[mapping.billingAddress]?.trim() : '';
    const billingAddress = rawBillingAddress || defaults.billingAddress?.trim() || undefined;

    // KYC is never taken from the sheet: imported users always start with KYC pending (R47).

    mappedRows.push({
      rowNumber,
      phoneNumber,
      companyName,
      fullName,
      email,
      gstin,
      billingAddress,
      secondaryPhoneNumber,
      whatsappNumber,
      rawValues: row,
      errors,
      isValid: errors.length === 0,
    });
  });

  // Pass 2: Check for duplicate phones within file
  let duplicatePhoneCount = 0;
  phoneOccurrences.forEach((rows, phone) => {
    if (rows.length > 1) {
      duplicatePhoneCount += rows.length;
      rows.forEach((rNum) => {
        const row = mappedRows[rNum - 1];
        row.errors.push(`Duplicate phone ${phone} (appears in rows ${rows.join(', ')})`);
        row.isValid = false;
      });
    }
  });

  let validRows = 0;
  let invalidRows = 0;
  let missingRequiredCount = 0;

  mappedRows.forEach((r) => {
    if (r.errors.length > 0) {
      r.isValid = false;
      invalidRows++;
      if (r.errors.some((e) => e.includes('required'))) {
        missingRequiredCount++;
      }
    } else {
      r.isValid = true;
      validRows++;
    }
  });

  return {
    totalRows: mappedRows.length,
    validRows,
    invalidRows,
    hasErrors: invalidRows > 0,
    duplicatePhoneCount,
    missingRequiredCount,
    mappedRows,
  };
}
